/**
 * IconButton + IconLink (Phase 26)
 *
 * Bouton/link compose d'un SVG (lucide-react) + tooltip texte au hover.
 * Le label est obligatoire pour l'accessibilite (aria-label) et sert
 * aussi de tooltip visuel.
 *
 * Variantes :
 *   - default : ghost (transparent, hover amber)
 *   - primary : bg-brand-500 (CTA principal)
 *   - secondary : bg-ink-800
 *   - danger : bg-red-900 (actions destructives)
 *
 * Tailles :
 *   - xs : 24×24 (badges)
 *   - sm : 32×32 (header, toolbar)
 *   - md : 40×40 (default, action principale)
 *   - lg : 48×48 (boutons flottants)
 */

import { Link, type LinkProps } from 'react-router-dom';
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { Tooltip } from './Tooltip';

export type { LucideIcon };

type Variant = 'default' | 'primary' | 'secondary' | 'danger';
type Size = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<Size, string> = {
  xs: 'w-6 h-6 [&>svg]:w-3 [&>svg]:h-3',
  sm: 'w-8 h-8 [&>svg]:w-4 [&>svg]:h-4',
  md: 'w-10 h-10 [&>svg]:w-5 [&>svg]:h-5',
  lg: 'w-12 h-12 [&>svg]:w-6 [&>svg]:h-6',
};

const VARIANT_CLASSES: Record<Variant, string> = {
  default:
    'text-steel-300 hover:text-brand-400 hover:bg-ink-800 border border-transparent',
  primary:
    'text-ink-950 bg-brand-500 hover:bg-brand-400 border border-brand-500',
  secondary:
    'text-steel-100 bg-ink-800 hover:bg-ink-700 border border-ink-700',
  danger:
    'text-red-300 hover:text-red-200 hover:bg-red-900/40 border border-transparent',
};

const COMMON =
  'inline-flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 disabled:opacity-40 disabled:cursor-not-allowed';

// ========================================================
// IconButton (button)
// ========================================================

type IconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> & {
  /** Composant SVG (depuis lucide-react), ex : <RotateCw /> */
  icon: LucideIcon;
  /** Texte lisible : sert d'aria-label + tooltip */
  label: string;
  variant?: Variant;
  size?: Size;
  /** Cote ou afficher le tooltip (default: bottom) */
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  /** Masque le tooltip (label affiche autrement) */
  hideTooltip?: boolean;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon: Icon,
      label,
      variant = 'default',
      size = 'md',
      tooltipSide = 'bottom',
      hideTooltip,
      className = '',
      type = 'button',
      ...rest
    },
    ref,
  ) {
    return (
      <Tooltip label={label} side={tooltipSide} disabled={hideTooltip}>
        <button
          ref={ref}
          type={type}
          aria-label={label}
          className={`${COMMON} ${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]} ${className}`}
          {...rest}
        >
          <Icon aria-hidden strokeWidth={1.75} />
        </button>
      </Tooltip>
    );
  },
);

// ========================================================
// IconLink (react-router Link)
// ========================================================

type IconLinkProps = Omit<LinkProps, 'children' | 'aria-label'> & {
  icon: LucideIcon;
  label: string;
  variant?: Variant;
  size?: Size;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  hideTooltip?: boolean;
};

export function IconLink({
  icon: Icon,
  label,
  variant = 'default',
  size = 'md',
  tooltipSide = 'bottom',
  hideTooltip,
  className = '',
  ...rest
}: IconLinkProps) {
  return (
    <Tooltip label={label} side={tooltipSide} disabled={hideTooltip}>
      <Link
        aria-label={label}
        className={`${COMMON} ${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]} ${className}`}
        {...rest}
      >
        <Icon aria-hidden strokeWidth={1.75} />
      </Link>
    </Tooltip>
  );
}

// ========================================================
// IconAnchor (lien externe <a>)
// ========================================================

type IconAnchorProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'children' | 'aria-label'
> & {
  icon: LucideIcon;
  label: string;
  variant?: Variant;
  size?: Size;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  hideTooltip?: boolean;
};

export function IconAnchor({
  icon: Icon,
  label,
  variant = 'default',
  size = 'md',
  tooltipSide = 'bottom',
  hideTooltip,
  className = '',
  target,
  rel,
  ...rest
}: IconAnchorProps) {
  // Externals : noopener par defaut
  const safeRel =
    target === '_blank' && !rel ? 'noopener noreferrer' : rel;

  return (
    <Tooltip label={label} side={tooltipSide} disabled={hideTooltip}>
      <a
        aria-label={label}
        target={target}
        rel={safeRel}
        className={`${COMMON} ${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]} ${className}`}
        {...rest}
      >
        <Icon aria-hidden strokeWidth={1.75} />
      </a>
    </Tooltip>
  );
}

// ========================================================
// IconButtonWithText : variante avec label visible (CTA "↻ refresh")
// ========================================================

type IconButtonWithTextProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: LucideIcon;
  /** Texte visible a cote de l'icone */
  children: ReactNode;
  variant?: Variant;
  size?: 'sm' | 'md';
};

export function IconButtonWithText({
  icon: Icon,
  children,
  variant = 'default',
  size = 'md',
  className = '',
  type = 'button',
  ...rest
}: IconButtonWithTextProps) {
  const sizeClass =
    size === 'sm' ? 'h-8 px-2.5 gap-1.5 text-xs [&>svg]:w-3.5 [&>svg]:h-3.5' : 'h-10 px-3 gap-2 text-sm [&>svg]:w-4 [&>svg]:h-4';
  return (
    <button
      type={type}
      className={`${COMMON} ${sizeClass} ${VARIANT_CLASSES[variant]} ${className}`}
      {...rest}
    >
      <Icon aria-hidden strokeWidth={1.75} />
      <span>{children}</span>
    </button>
  );
}
